/**
 * @fileoverview Cartão de contato compartilhado (vCard) dentro do balão do chat.
 * Um contato: linha com nome e número e rodapé Conversar | Copiar | Salvar.
 * Vários: "N contatos", uma linha por contato com as mesmas ações em ícone,
 * os 3 primeiros e "Ver todos" (o resto abre e fecha com animação de altura).
 * Foto de perfil das linhas visíveis, pedida quando o cartão aparece na tela
 * (WAHA; na API oficial da Meta fica o ícone).
 * Superfícies translúcidas para funcionar no balão branco/cinza (recebida) e
 * no verde (enviada).
 */

import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Collapse from "@material-ui/core/Collapse";
import Avatar from "@material-ui/core/Avatar";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import { UserRound, MessageCircle, Copy, Check, UserPlus, ChevronDown } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { contactNumberLabel } from "../../utils/sharedContacts";
import { useCopyFeedback } from "../../hooks/useCopyFeedback";
import { useContactPictures } from "../../hooks/useContactPictures";
import StartConversationModal from "../StartConversationModal";
import usePermission from "../../hooks/usePermission";

const VISIBLE = 3;
const EXPAND_MS = 260;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const muted = dark ? "rgba(233,237,239,0.62)" : "rgba(17,27,33,0.62)";
  const divider = dark ? "rgba(255,255,255,0.1)" : "rgba(11,20,26,0.1)";
  const hover = dark ? "rgba(255,255,255,0.07)" : "rgba(11,20,26,0.05)";
  const ok = dark ? "#00a884" : "#008069";
  const t = "background 140ms ease, color 140ms ease";

  return {
    root: {
      whiteSpace: "normal",
      minWidth: 240,
      maxWidth: 320,
      display: "flex",
      flexDirection: "column",
      marginTop: 2,
    },
    eyebrow: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: muted,
      padding: "2px 4px 4px",
    },
    row: { display: "flex", alignItems: "center", gap: 10, padding: "6px 4px", minWidth: 0 },
    rowDivided: { borderTop: `1px solid ${divider}` },
    avatar: {
      width: 40,
      height: 40,
      borderRadius: "50%",
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: dark ? "rgba(255,255,255,0.1)" : "rgba(11,20,26,0.07)",
      color: muted,
    },
    avatarSmall: { width: 32, height: 32 },
    text: { display: "flex", flexDirection: "column", minWidth: 0, flex: 1 },
    name: {
      fontSize: 14.5,
      fontWeight: 500,
      lineHeight: 1.3,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    number: {
      fontSize: 12.5,
      color: muted,
      fontVariantNumeric: "tabular-nums",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    footer: { display: "flex", marginTop: 4, borderTop: `1px solid ${divider}` },
    footerBtn: {
      flex: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      height: 36,
      padding: "0 6px",
      border: "none",
      background: "transparent",
      color: "inherit",
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: t,
      "& + &": { borderLeft: `1px solid ${divider}` },
      "&:hover:not(:disabled)": { background: hover },
      "&:disabled": { opacity: 0.45, cursor: "default" },
      "&:focus-visible": { outline: `2px solid ${muted}`, outlineOffset: -2 },
      "& svg": { width: 15, height: 15, flexShrink: 0 },
    },
    actions: { display: "flex", gap: 2, flexShrink: 0 },
    iconBtn: {
      width: 30,
      height: 30,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      borderRadius: 8,
      background: "transparent",
      color: muted,
      cursor: "pointer",
      transition: t,
      "&:hover:not(:disabled)": { background: hover, color: "inherit" },
      "&:disabled": { opacity: 0.45, cursor: "default" },
      "&:focus-visible": { outline: `2px solid ${muted}`, outlineOffset: 1 },
      "& svg": { width: 16, height: 16 },
    },
    done: { color: `${ok} !important` },
    more: {
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      border: "none",
      background: "transparent",
      fontFamily: "inherit",
      fontSize: 12.5,
      fontWeight: 600,
      color: muted,
      padding: "6px 4px 2px",
      cursor: "pointer",
      "&:hover": { color: "inherit" },
      "&:focus-visible": { outline: `2px solid ${muted}`, outlineOffset: 1, borderRadius: 4 },
      "& svg": { width: 14, height: 14, transition: `transform ${EXPAND_MS}ms ease` },
      "@media (prefers-reduced-motion: reduce)": { "& svg": { transition: "none" } },
    },
    moreOpen: { "& svg": { transform: "rotate(180deg)" } },
  };
});

const numberLabel = (digits) => contactNumberLabel(digits) || i18n.t("sharedContactCard.noNumber");

const ContactRow = ({ contact, picture, compact, divided, disabled }) => {
  const classes = useStyles();
  const [talking, setTalking] = useState(false);
  const [saveState, setSaveState] = useState(null); // null | "saving" | "saved"
  const { copied, copy } = useCopyFeedback(contact.number, {
    copiedMessage: i18n.t("sharedContactCard.copiedToast"),
    failedMessage: i18n.t("sharedContactCard.copyFailed"),
  });
  const off = disabled || !contact.number;
  const displayName = contact.name || numberLabel(contact.number);
  // Conversar cria o contato, se novo, e a conversa; salvar cria o contato:
  // hoje, abertos a quem atende; com a regra nova, tickets.create e contacts.edit.
  const { can } = usePermission();

  const save = async () => {
    setSaveState("saving");
    try {
      // "+" garante que número estrangeiro não ganhe o 55 no backend.
      await api.post("/contacts", { name: displayName, number: `+${contact.number}` });
      setSaveState("saved");
      toast.success(i18n.t("sharedContactCard.savedToast"));
    } catch (err) {
      if (err?.response?.data?.error === "ERR_DUPLICATED_CONTACT") {
        setSaveState("saved");
        toast.info(i18n.t("backendErrors.ERR_DUPLICATED_CONTACT"));
        return;
      }
      setSaveState(null);
      toastError(err);
    }
  };

  const actions = [
    {
      key: "talk",
      label: i18n.t("sharedContactCard.talk"),
      icon: <MessageCircle />,
      onClick: () => setTalking(true),
      allowed: can("tickets.create", true) && can("contacts.edit", true),
    },
    {
      key: "copy",
      label: i18n.t(copied ? "sharedContactCard.copied" : "sharedContactCard.copy"),
      icon: copied ? <Check /> : <Copy />,
      onClick: copy,
      done: copied,
    },
    {
      key: "save",
      label: i18n.t(
        saveState === "saved" ? "sharedContactCard.saved" : saveState === "saving" ? "sharedContactCard.saving" : "sharedContactCard.save"
      ),
      icon: saveState === "saving" ? <CircularProgress size={14} color="inherit" /> : saveState === "saved" ? <Check /> : <UserPlus />,
      onClick: save,
      done: saveState === "saved",
      busy: saveState !== null,
      allowed: can("contacts.edit", true),
    },
  ].filter((a) => a.allowed !== false);

  return (
    <>
      <div className={`${classes.row} ${divided ? classes.rowDivided : ""}`}>
        <Avatar
          src={picture || undefined}
          alt=""
          aria-hidden="true"
          className={`${classes.avatar} ${compact ? classes.avatarSmall : ""}`}
        >
          <UserRound size={compact ? 16 : 20} strokeWidth={1.8} />
        </Avatar>
        <span className={classes.text}>
          <span className={classes.name} title={displayName}>{displayName}</span>
          {contact.name && <span className={classes.number}>{numberLabel(contact.number)}</span>}
        </span>
        {compact && (
          <span className={classes.actions}>
            {actions.map((a) => (
              <Tooltip key={a.key} title={a.label} placement="top">
                <span>
                  <button
                    type="button"
                    className={`${classes.iconBtn} ${a.done ? classes.done : ""}`}
                    onClick={a.onClick}
                    disabled={off || a.busy}
                    aria-label={`${a.label}: ${displayName}`}
                  >
                    {a.icon}
                  </button>
                </span>
              </Tooltip>
            ))}
          </span>
        )}
      </div>
      {!compact && (
        <div className={classes.footer}>
          {actions.map((a) => (
            <button
              key={a.key}
              type="button"
              className={`${classes.footerBtn} ${a.done ? classes.done : ""}`}
              onClick={a.onClick}
              disabled={off || a.busy}
            >
              {a.icon}
              {a.label}
            </button>
          ))}
        </div>
      )}
      {talking && (
        <StartConversationModal
          open
          onClose={() => setTalking(false)}
          phoneNumber={`+${contact.number}`}
          contactName={contact.name}
        />
      )}
    </>
  );
};

const SharedContactCard = ({ contacts, ticketId, disabled = false }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const rootRef = useRef(null);
  const list = contacts || [];
  // Fotos só das linhas à mostra: as 3 primeiras e, aberto, todas.
  const shown = list.length <= VISIBLE || expanded ? list : list.slice(0, VISIBLE);
  const pictureOf = useContactPictures(ticketId, shown.map((c) => c.number), visible);

  // O chat tem muitas mensagens: as fotos são pedidas quando o cartão aparece.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || visible) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  if (!list.length) return null;

  if (list.length === 1) {
    return (
      <div className={classes.root} ref={rootRef}>
        <ContactRow contact={list[0]} picture={pictureOf(list[0].number)} disabled={disabled} />
      </div>
    );
  }

  const first = list.slice(0, VISIBLE);
  const rest = list.slice(VISIBLE);
  const reduceMotion = prefersReducedMotion();

  // Lista longa fechada: o cartão volta para a vista (o chat não fica num ponto vazio).
  const handleCollapsed = () => {
    const el = rootRef.current;
    if (el?.scrollIntoView) el.scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
  };

  return (
    <div className={classes.root} ref={rootRef}>
      <div className={classes.eyebrow}>{i18n.t("sharedContactCard.contacts", { count: list.length })}</div>
      {first.map((contact, i) => (
        <ContactRow
          key={`${contact.number}-${i}`}
          contact={contact}
          picture={pictureOf(contact.number)}
          compact
          divided={i > 0}
          disabled={disabled}
        />
      ))}
      {rest.length > 0 && (
        <>
          {/* O resto monta na primeira abertura e fica montado para animar o fechamento. */}
          <Collapse in={expanded} timeout={reduceMotion ? 0 : EXPAND_MS} mountOnEnter onExited={handleCollapsed}>
            {rest.map((contact, i) => (
              <ContactRow
                key={`${contact.number}-${i + VISIBLE}`}
                contact={contact}
                picture={pictureOf(contact.number)}
                compact
                divided
                disabled={disabled}
              />
            ))}
          </Collapse>
          <button
            type="button"
            className={`${classes.more} ${expanded ? classes.moreOpen : ""}`}
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded
              ? i18n.t("sharedContactCard.showLess")
              : i18n.t("sharedContactCard.showAll", { count: list.length })}
            <ChevronDown aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
};

export default SharedContactCard;
